import React, { useState } from 'react';
import { Avatar } from './Avatar';
import { parseFormattedText, cn } from '@/lib/utils';
import {
    Undo2,
    Phone,
    Mail,
    MessageCircle,
    Folder,
    FileText,
    BookOpen,
    Book,
    Calendar,
    Clock,
    MapPin,
    ShoppingCart,
    CreditCard,
    Download,
    Upload,
    Link,
    ExternalLink,
    User,
    Users,
    Heart,
    Star,
    Settings,
    HelpCircle,
    Info,
    AlertCircle,
    CheckCircle,
    type LucideIcon,
} from 'lucide-react';
import type { OptionsMessage as OptionsMessageType } from '@/types';

// Map icon names to Lucide icon components
const iconMap: Record<string, LucideIcon> = {
    'phone': Phone,
    'mail': Mail,
    'message-circle': MessageCircle,
    'folder': Folder,
    'file-text': FileText,
    'book-open': BookOpen,
    'book': Book,
    'calendar': Calendar,
    'clock': Clock,
    'map-pin': MapPin,
    'shopping-cart': ShoppingCart,
    'credit-card': CreditCard,
    'download': Download,
    'upload': Upload,
    'link': Link,
    'external-link': ExternalLink,
    'user': User,
    'users': Users,
    'heart': Heart,
    'star': Star,
    'settings': Settings,
    'help-circle': HelpCircle,
    'info': Info,
    'alert-circle': AlertCircle,
    'check-circle': CheckCircle,
};

interface OptionsMessageProps {
    message: OptionsMessageType;
    botAvatar: string;
    customStyles?: {
        primaryColor?: string;
        secondaryColor?: string;
        botMessageBg?: string;
        botMessageText?: string;
        borderRadius?: number;
        fontSize?: number;
    };
    onOptionSelect?: (value: string) => void;
    onUndo?: () => void;
    disabled?: boolean;
}

const getGridClass = (showPerRow: number) => {
    const gridClasses: Record<number, string> = {
        1: 'options-grid-1',
        2: 'options-grid-2',
        3: 'options-grid-3',
    };
    return gridClasses[Math.min(Math.max(showPerRow, 1), 3)] || 'options-grid-1';
};

export const OptionsMessage: React.FC<OptionsMessageProps> = ({
    message,
    botAvatar,
    customStyles,
    onOptionSelect,
    onUndo,
    disabled = false,
}) => {
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [hoveredOption, setHoveredOption] = useState<string | null>(null);

    // Get custom colors with fallbacks
    const primaryColor = customStyles?.primaryColor || '#a855f7';
    const secondaryColor = customStyles?.secondaryColor || '#764ba2';
    const botBgColor = customStyles?.botMessageBg || secondaryColor || '#ffffff';
    const borderRadius = customStyles?.borderRadius || 16;
    const botTextColor = customStyles?.botMessageText ?? '#1f2937';
    const fontSize = customStyles?.fontSize ?? 14;
    const showBackground = message.showBackground ?? false;

    const handleOptionClick = (optionValue: string, option: any) => {
        // Don't allow clicks if externally disabled
        if (disabled) return;

        setSelectedOption(optionValue);

        // Handle link redirect if present
        if (option.link) {
            const delay = option.redirectDelayMs || 500; // Default 500ms
            setTimeout(() => {
                window.open(option.link, '_blank');
            }, delay);
        }

        if (onOptionSelect) {
            onOptionSelect(optionValue);
        }
    };

    const showPerRow = message.showPerRow || 1;
    const isBars = message.displayStyle === 'bars';

    // Reset selection when disabled changes from true to false (after undo)
    React.useEffect(() => {
        if (!disabled && selectedOption !== null) {
            setSelectedOption(null);
        }
    }, [disabled]);

    // Debug logging for image detection
    React.useEffect(() => {
        console.log('[OptionsMessage] Options:', message.options.map(opt => ({
            text: opt.text,
            hasImage: !!(opt.image && opt.image.url),
            imageUrl: opt.image?.url
        })));
    }, [message.options]);

    // Determine if options should be disabled (either externally or by local selection)
    const isDisabled = disabled || selectedOption !== null;

    return (
        <div className="zentri-flex zentri-items-start zentri-gap-3 zentri-mb-4 zentri-animate-fade-in">
            <Avatar src={botAvatar} alt="Bot" size="md" />

            <div className="zentri-flex zentri-flex-col zentri-gap-2.5 zentri-max-w-lg zentri-group">
                {/* Question text */}
                <div
                    className="zentri-px-3.5 zentri-py-2.5 zentri-flex zentri-items-center zentri-justify-between zentri-animate-fade-slide-up zentri-transition-all zentri-duration-300 zentri-border zentri-border-neutral-100"
                    style={{
                        backgroundColor: botBgColor,
                        borderRadius: `${borderRadius}px`,
                        borderTopLeftRadius: '0',
                        borderBottomRightRadius: `${borderRadius}px`,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    }}
                >
                    {/* Question content */}
                    <div
                        className="zentri-leading-relaxed"
                        style={{
                            color: botTextColor,
                            fontSize: `${fontSize}px`,
                            ['--zentri-primary' as string]: primaryColor,
                        }}
                        dangerouslySetInnerHTML={parseFormattedText(message.text)}
                    />
                    {onUndo && (
                        <button
                            onClick={onUndo}
                            className="zentri-opacity-0 zentri-group-hover:zentri-opacity-100 zentri-transition-all zentri-duration-300 zentri-p-1 zentri-rounded-md hover:zentri-bg-neutral-100 zentri-text-neutral-500 hover:zentri-text-neutral-700 zentri-flex-shrink-0 zentri-ml-2 hover:zentri-scale-110"
                            title="Undo message"
                            aria-label="Undo message"
                        >
                            <Undo2 size={14} strokeWidth={2} />
                        </button>
                    )}
                </div>

                {/* Options - optional common card background (secondaryColor) with padding */}
                <div
                    className={cn(getGridClass(showPerRow), showBackground && 'zentri-rounded-xl')}
                    style={showBackground ? { backgroundColor: secondaryColor, padding: '10px' } : undefined}
                >
                    {message.options.map((option, index) => {
                        const hasImage = option.image && option.image.url;
                        const IconComponent = option.icon ? iconMap[option.icon] : null;
                        const hasVisual = hasImage || !!IconComponent;
                        const optCustom = option.customStyle;
                        const optBg = optCustom?.backgroundColor || botBgColor;
                        const optBorder = optCustom?.borderColor;
                        const optText = optCustom?.textColor;

                        // Bar layout: horizontal row with small visual to the left
                        const useRowLayout = isBars && hasVisual;

                        return (
                            <button
                                key={index}
                                onClick={() => handleOptionClick(option.value, option)}
                                onMouseEnter={() => setHoveredOption(option.value)}
                                onMouseLeave={() => setHoveredOption(null)}
                                disabled={isDisabled}
                                className={cn(
                                    'option-button',
                                    useRowLayout
                                        ? 'zentri-flex zentri-flex-row zentri-items-center zentri-gap-3'
                                        : 'zentri-flex zentri-flex-col zentri-gap-2.5',
                                    'zentri-px-4 zentri-py-3 zentri-rounded-xl',
                                    'zentri-border zentri-bg-white zentri-shadow-card',
                                    'zentri-transition-all zentri-duration-300 zentri-ease-out',
                                    'focus:zentri-outline-none focus:zentri-ring-2 focus:zentri-ring-offset-0',
                                    'zentri-animate-fade-slide-up zentri-w-full',
                                    'zentri-cursor-pointer',
                                    // Minimum height for options without large visuals
                                    (!hasImage || isBars) && 'zentri-min-h-[52px]',
                                    // Text alignment based on layout
                                    useRowLayout
                                        ? '' // row layout: left-aligned by default
                                        : hasImage ? 'zentri-items-stretch' : 'zentri-items-center zentri-justify-center',
                                    isDisabled && selectedOption !== option.value
                                        ? 'zentri-opacity-40 zentri-cursor-not-allowed'
                                        : ''
                                )}
                                style={{
                                    animationDelay: `${index * 0.08}s`,
                                    borderColor: selectedOption === option.value
                                        ? (optBorder || primaryColor)
                                        : hoveredOption === option.value
                                            ? (optBorder ? `${optBorder}AA` : `${primaryColor}AA`)
                                            : (optBorder || '#e5e7eb'),
                                    backgroundColor: optBg,
                                    boxShadow: selectedOption === option.value
                                        ? `0 8px 20px ${optBorder || primaryColor}33`
                                        : hoveredOption === option.value
                                            ? `0 6px 16px ${optBorder || primaryColor}22`
                                            : '0 4px 12px rgba(0, 0, 0, 0.08)',
                                }}
                            >
                                {/* Option image */}
                                {hasImage && option.image && (
                                    isBars ? (
                                        // Bar mode: small thumbnail to the left
                                        <div className="zentri-w-8 zentri-h-8 zentri-rounded-md zentri-overflow-hidden zentri-bg-neutral-100 zentri-flex-shrink-0">
                                            <img
                                                src={option.image.url}
                                                alt={option.image.alt || option.text}
                                                className="zentri-w-full zentri-h-full zentri-object-cover"
                                                loading="lazy"
                                            />
                                        </div>
                                    ) : (
                                        // Card mode: full-width card image
                                        <div className="zentri-w-full zentri-aspect-video zentri-rounded-lg zentri-overflow-hidden zentri-bg-neutral-100 zentri-transition-all zentri-duration-300 zentri-mb-1">
                                            <img
                                                src={option.image.url}
                                                alt={option.image.alt || option.text}
                                                className={cn(
                                                    'zentri-w-full zentri-h-full zentri-object-cover zentri-transition-transform zentri-duration-300',
                                                    hoveredOption === option.value && 'zentri-scale-110'
                                                )}
                                                loading="lazy"
                                            />
                                        </div>
                                    )
                                )}

                                {/* Option icon (shown when no image is present, only in bar mode) */}
                                {!hasImage && IconComponent && isBars && (
                                    <IconComponent
                                        size={20}
                                        strokeWidth={2}
                                        className="zentri-flex-shrink-0"
                                        style={{ color: optText || primaryColor }}
                                    />
                                )}

                                {/* Option text */}
                                <span
                                    className={cn(
                                        'zentri-font-medium zentri-leading-snug zentri-transition-all zentri-duration-300',
                                        useRowLayout
                                            ? 'zentri-text-left'
                                            : hasImage
                                                ? 'zentri-text-left zentri-w-full'
                                                : 'zentri-text-center'
                                    )}
                                    style={{
                                        color: optText || '#1f2937',
                                        fontSize: `${fontSize}px`,
                                    }}
                                >
                                    {option.text}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
